import {
  AggregateVariable,
  AppDomainsAggregatesModelsOrderByVariable,
  type GetInteractionAggregatesReqFiltersItem,
  type QueryOrdering,
  type TimeFilterConfigGranularity,
  type TimeRangeConfig,
} from '@/api/generated/model';
import { AggregateBy } from '@/domains/dashboard/components/GlobalfiltersRow';
import { resolveEffectiveGranularity } from '@/utils/granularity';

type Options = {
  aggregateBy: AggregateBy;
};

export const variablesInteractionConversationMap: Partial<Record<AggregateVariable, AggregateVariable>> = {
  n_topics: 'n_topics_conversations',
  topics: 'topics_conversations',
  n_user_actions: 'n_user_actions_conversations',
  percentage_negative_feedback: 'conversation_percentage_negative_feedback',
  percentage_negative_feedback_explicit: 'conversation_percentage_negative_feedback_explicit',
  sentiment_score: 'conversation_sentiment_score',
  n_interactions: 'n_sessions',
} as const;

/**
 *
 * each aggregate variable when aggregateBy='interaction'
 * has a counterpart when aggregateBy='conversation'
 *
 * so whenever we switch aggregateBy from 'interaction' to 'conversation',
 * we need to transform the variables to their counterpart
 *
 */
export const mapAggregateVariables = (variables: AggregateVariable[], opts: Options) => {
  const { aggregateBy } = opts;

  const transformedVariables: AggregateVariable[] = variables.map((variable) => {
    if (aggregateBy === 'conversation') {
      const conversationCounterpart =
        variable in variablesInteractionConversationMap ? variablesInteractionConversationMap[variable] : null;

      if (!conversationCounterpart) return variable;

      return conversationCounterpart;
    }

    if (aggregateBy === 'interaction') {
      const interactionCounterpart = Object.keys(variablesInteractionConversationMap).find(
        (key) => variablesInteractionConversationMap[key as AggregateVariable] === variable,
      );

      if (!interactionCounterpart) return variable;

      return interactionCounterpart as AggregateVariable;
    }

    return variable;
  });

  return transformedVariables;
};

export const mapAggregateOrdering = (ordering: QueryOrdering, opts: Options): QueryOrdering => {
  return {
    ...ordering,
    by: ordering.by ? mapAggregateOrderBy(ordering.by, opts) : undefined,
  };
};

export const mapAggregateOrderBy = (by: QueryOrdering['by'], opts: Options) => {
  return mapAggregateVariables([by as AggregateVariable], opts)[0] as AppDomainsAggregatesModelsOrderByVariable;
};

function getAggregatedDataTimeFilterGranularity(
  granularity: string | undefined,
  timeRange: TimeRangeConfig | undefined,
): TimeFilterConfigGranularity {
  const resolved = resolveEffectiveGranularity(granularity, timeRange);
  if (resolved === 'week' || resolved === 'month') {
    return resolved;
  }

  return 'day';
}

export function normalizeAggregatedDataTimeFilters<T extends GetInteractionAggregatesReqFiltersItem>(
  filters: T[],
  granularity: string | undefined,
  timeRange: TimeRangeConfig | undefined,
): T[] {
  const timeFilterGranularity = getAggregatedDataTimeFilterGranularity(granularity, timeRange);

  return filters.map((filter) => {
    if (filter.column !== 'time') {
      return filter;
    }

    return {
      ...filter,
      granularity: timeFilterGranularity,
    } as T;
  });
}
